import { useState } from 'react'

import {
  Button,
  ChevronDownIcon,
  DropdownMenu,
  Tabs,
  useIsNarrowPageHeader,
} from '@buffer-mono/popcorn'

import { SplitTagBadge } from '~publish/components/SplitTagBadge'

import { DMS_TAB_SPLIT } from '../../hooks/navigation/useAvailableTabs'
import { MentionsAvailableCoachmark } from '../../Mentions/MentionsAvailableCoachmark'
import { TAB_LABELS } from '../../types'
import type { CommunityTab } from '../../types'
import { CommentsTabIndicator } from '../CommentsTabIndicator'
import { MentionsTabIndicator } from '../MentionsTabIndicator'

import styles from './CommunityTabBar.module.css'

import type { JSX } from 'react'

type CommunityTabBarProps = {
  tabs: CommunityTab[]
  value: CommunityTab
  onChange: (tab: CommunityTab) => void
  commentsCount: number | null
  mentionsCount: number | null
  mentionsSupported: boolean
}

type TabIndicatorProps = {
  tab: CommunityTab
  commentsCount: number | null
  mentionsCount: number | null
}

function TabIndicator({
  tab,
  commentsCount,
  mentionsCount,
}: TabIndicatorProps): JSX.Element | null {
  if (tab === 'comments') return <CommentsTabIndicator count={commentsCount} />
  if (tab === 'mentions') return <MentionsTabIndicator count={mentionsCount} />
  return <SplitTagBadge splitName={DMS_TAB_SPLIT} />
}

export function CommunityTabBar({
  tabs,
  value,
  onChange,
  commentsCount,
  mentionsCount,
  mentionsSupported,
}: CommunityTabBarProps): JSX.Element {
  const isNarrowHeader = useIsNarrowPageHeader() ?? false
  const [menuOpen, setMenuOpen] = useState(false)

  if (isNarrowHeader) {
    const currentTab = tabs.includes(value) ? value : tabs[0]
    const menu = (
      <DropdownMenu open={menuOpen} onOpenChange={setMenuOpen}>
        <DropdownMenu.Trigger>
          <Button variant="tertiary" className={styles.trigger}>
            {TAB_LABELS[currentTab]}
            <TabIndicator
              tab={currentTab}
              commentsCount={commentsCount}
              mentionsCount={mentionsCount}
            />
            <ChevronDownIcon />
          </Button>
        </DropdownMenu.Trigger>
        <DropdownMenu.Content align="start">
          <DropdownMenu.RadioGroup
            value={currentTab}
            onChange={(newValue): void => {
              setMenuOpen(false)
              const tab = tabs.find((t) => t === newValue)
              if (tab) onChange(tab)
            }}
          >
            {tabs.map((t) => (
              <DropdownMenu.RadioItem key={t} value={t} className={styles.item}>
                {TAB_LABELS[t]}
                <TabIndicator
                  tab={t}
                  commentsCount={commentsCount}
                  mentionsCount={mentionsCount}
                />
              </DropdownMenu.RadioItem>
            ))}
          </DropdownMenu.RadioGroup>
        </DropdownMenu.Content>
      </DropdownMenu>
    )
    return tabs.includes('mentions') ? (
      <MentionsAvailableCoachmark mentionsSupported={mentionsSupported}>
        {menu}
      </MentionsAvailableCoachmark>
    ) : (
      menu
    )
  }

  return (
    <Tabs.TabList>
      {tabs.map((t) => {
        const tabElement = (
          <Tabs.Tab key={t} value={t}>
            {TAB_LABELS[t]}
            <TabIndicator
              tab={t}
              commentsCount={commentsCount}
              mentionsCount={mentionsCount}
            />
          </Tabs.Tab>
        )
        return t === 'mentions' ? (
          <MentionsAvailableCoachmark
            key={t}
            mentionsSupported={mentionsSupported}
          >
            {tabElement}
          </MentionsAvailableCoachmark>
        ) : (
          tabElement
        )
      })}
    </Tabs.TabList>
  )
}
